import { IS_APP } from "@/__main__/constants.mjs";
import { QUEUE_SYMBOLS } from "@/game-lol/constants/constants-queue-symbols.mjs";
import { catchAllRoutes } from "@/routes/constants.mjs";
import type { BaseRoute } from "@/routes/routes.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import pathRegExp from "@/util/path-regexp.mjs";

const lolFetchChampionData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-champion-data.mjs"),
);
const lolFetchChampionsData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-champions-data.mjs"),
);
const lolFetchPostmatchData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-postmatch-data.mjs"),
);
const lolFetchProBuildsData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-probuilds-data.mjs"),
);
const lolFetchProfileData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-profile-overview-data.mjs"),
);

const lolFetchProfileChampionsData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-profile-champions-data.mjs"),
);
// const lolFetchSeasonReview = lazyFn(
//   () => import("@/game-lol/fetches/lol-fetch-season-review-data.mjs"),
// );
const lolFetchStaticData = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-static-data.mjs"),
);
const lolFetchArenaAugments = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-arena-augments.mjs"),
);
const fetchArticle = lazyFn(
  () => import("@/shared/fetch-contentful-article.mjs"),
);
const fetchGuidesList = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-guides-list.mjs"),
);
const fetchNewsList = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-news-list.mjs"),
);
const lolFetchDoombotsLeaderboard = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-doombots-leaderboard.mjs"),
);
const lolFetchAramMayhemAugmentsStats = lazyFn(
  () => import("@/game-lol/fetches/lol-fetch-aram-mayhem-augments-stats.mjs"),
);

export const overallTabs = [
  "overview",
  "aram",
  "arena-duos",
  "arena",
  "nexus-blitz",
  "urf",
  "one-for-all",
  "doombots",
  "synergies",
  "aram-mayhem",
].join("|");

interface ChampionTab {
  useParams: boolean;
  url: string;
  routeMatch: RegExp;
  live: boolean;
  name: Translation;
  queue?: symbol;
  date?: string;
  titleMetaInfo: (params: Record<string, string | Translation>) => Translation;
}

export const CHAMPION_TABS = {
  build: {
    useParams: true,
    url: `/build`,
    routeMatch: /build/,
    live: true,
    name: IS_APP
      ? ["lol:championsPage.tabs.overview", "Overview"]
      : ["lol:championsPage.tabs.build", "Build"],
    queue: QUEUE_SYMBOLS.rankedSoloDuo,
    date: "",
    titleMetaInfo: ({ patchNumber, role, rank }) => {
      return [
        "lol:championspage.tabs.overviewTitle",
        "Build for {{role}}, {{tier}} Patch {{patchNumber}}",
        { role, tier: rank, patchNumber },
      ];
    },
  },
  aram: {
    useParams: false,
    url: `/aram`,
    routeMatch: /aram/,
    live: true,
    name: ["lol:championsPage.tabs.aram", "ARAM"],
    queue: QUEUE_SYMBOLS.aram,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.aramTitle",
        "ARAM Builds & Runes Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  "aram-mayhem": {
    useParams: false,
    url: `/aram-mayhem`,
    routeMatch: /aram-mayhem/,
    live: true,
    name: ["lol:championsPage.tabs.aram-mayhem", "ARAM Mayhem"],
    queue: QUEUE_SYMBOLS.aramMayhem,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.aramMayhemTitle",
        "ARAM Mayhem Builds & Augments Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  "one-for-all": {
    useParams: false,
    url: `/one-for-all`,
    routeMatch: /one-for-all/,
    live: false,
    name: ["lol:queueTypes.oneForAll", "One For All"],
    queue: QUEUE_SYMBOLS.oneForAll,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.oneForAllTitle",
        "One For All Builds & Runes Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  arena: {
    useParams: false,
    url: `/arena`,
    routeMatch: /arena/,
    live: true,
    name: ["lol:arena", "Arena"],
    queue: QUEUE_SYMBOLS.arena,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.arenaTitleWithPatch",
        "Arena Builds Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  "nexus-blitz": {
    useParams: false,
    url: `/nexus-blitz`,
    routeMatch: /nexus-blitz/,
    live: false,
    name: ["lol:queueTypes.nexus_blitz", "Nexus Blitz"],
    queue: QUEUE_SYMBOLS.nexusBlitz,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.nexusBlitzTitle",
        "Nexus Blitz Builds & Runes Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  urf: {
    useParams: false,
    url: `/urf`,
    routeMatch: /urf/,
    live: false,
    name: ["lol:queueTypes.urf", "URF"],
    queue: QUEUE_SYMBOLS.urf,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.urfTitle",
        "Urf Builds Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  doombots: {
    useParams: false,
    url: `/doombots`,
    routeMatch: /doombots/,
    live: false,
    name: ["lol:queueTypes.doombots", "Doombots"],
    queue: QUEUE_SYMBOLS.doomBots,
    date: "2025-08-27T12:00:00.000Z",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.doombotsTitle",
        "Doombots Builds Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  probuilds: {
    useParams: true,
    url: `/probuilds`,
    routeMatch: /probuilds/,
    live: true,
    name: ["lol:championsPage.tabs.probuilds", "Pro Builds"],
    queue: QUEUE_SYMBOLS.rankedSoloDuo,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.proBuildsTitle",
        "Pro Builds Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  trends: {
    useParams: true,
    url: `/trends`,
    routeMatch: /trends/,
    live: false,
    name: ["lol:championsPage.tabs.trends", "Trends"],
    queue: QUEUE_SYMBOLS.rankedSoloDuo,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.trendsTitle",
        "Trends Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  counters: {
    useParams: true,
    url: `/counters`,
    routeMatch: /counters/,
    live: true,
    name: ["lol:championsPage.tabs.counters", "Counters"],
    queue: QUEUE_SYMBOLS.rankedSoloDuo,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.countersTitle",
        "Counters Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  synergies: {
    useParams: true,
    url: `/synergies`,
    routeMatch: /synergies/,
    live: true,
    name: ["lol:championsPage.tabs.synergies", "Synergies"],
    queue: QUEUE_SYMBOLS.rankedSoloDuo,
    date: "2025-04-08T12:00:00.000Z",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.synergiesTitle",
        "Synergies Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
  abilities: {
    useParams: false,
    url: `/abilities`,
    routeMatch: /abilities/,
    live: true,
    name: ["lol:championsPage.tabs.abilities", "Abilities"],
    queue: null,
    date: "",
    titleMetaInfo: ({ patchNumber }) => {
      return [
        "lol:championspage.tabs.abilitiesTitle",
        "Abilities Patch {{patchNumber}}",
        { patchNumber },
      ];
    },
  },
} satisfies Record<string, ChampionTab>;

export const lolChampTabs = Object.keys(CHAMPION_TABS).join("|");

export const championsRedirectRoute = {
  path: /^\/lol\/champions/,
  redirect: "/lol/champions/overview",
};

export const championsRoute = {
  path: pathRegExp(`/lol/champions/:tab(${overallTabs})`),
  component: "game-lol/Statistics.tsx",
  fetchData: lolFetchChampionsData,
};

export const championRoute = {
  path: (() => {
    return new RegExp(
      `^/lol/champions/((?!(${overallTabs}))\\w+)(/(${lolChampTabs})(/(\\w*))?)$`, // eslint-disable-line no-useless-escape
    );
  })(),
  component: "game-lol/Champion.tsx",
  fetchData: lolFetchChampionData,
};

export const championRedirectRoute: BaseRoute = {
  path: new RegExp(
    `^/lol/champions/((?!(${overallTabs}))\\w+)(/overview)?$`, // eslint-disable-line no-useless-escape
  ),
  redirect: (route) => {
    const championKey = route.parameters[0];
    return championKey
      ? `/lol/champions/${championKey}/build`
      : `/lol/champions/overview`;
  },
};

export const tierlistRoute = {
  path: pathRegExp(
    "/lol/tierlist{/:tab(aram|arena|arena-duos|nexus-blitz|one-for-all|urf|doombots|aram-mayhem)}?",
  ),
  component: "game-lol/TierList.jsx",
  fetchData: lolFetchChampionsData,
};

export const probuildsRedirectRoute = {
  path: "/lol/probuilds",
  redirect: "/lol/probuilds/history",
};

export const probuildsRoute = {
  path: pathRegExp("/lol/probuilds/history"),
  component: "game-lol/ProBuilds.jsx",
  fetchData: lolFetchProBuildsData,
};

export const postMatchRoute = {
  path: pathRegExp("/lol/match/:region/:name/:id{/:tab}?{/:tabId}?"),
  component: "game-lol/PostMatch.jsx",
  fetchData: lolFetchPostmatchData,
  key: "lol-postmatch",
};

export const inGameRoute = {
  path: "/lol/in-game",
  component: "game-lol/in-game.mjs",
  fetchData() {
    // This is intentionally a no-op, do not put anything in here.
    // The reasoning is that this route needs to be able to operate independently
    // of which route the app is actually on, including fetching data.
  },
};

const routes: BaseRoute[] = [
  // Game loop
  postMatchRoute,
  inGameRoute,

  // Profile
  {
    path: pathRegExp("/lol/profile/:region/:name"),
    component: "game-lol/ProfileOverview.jsx",
    fetchData: lolFetchProfileData,
  },
  // {
  //   path: pathRegExp("/lol/profile/:region/:name/performance"),
  //   component: "game-lol/ProfilePerformance.tsx",
  //   fetchData: lolFetchProfilePerformanceData,
  // },
  {
    path: pathRegExp(
      "/lol/profile/:region/:name/champions{/:role}?{/:champion}?",
    ),
    component: "game-lol/ProfileChampions.jsx",
    fetchData: lolFetchProfileChampionsData,
  },

  // Statistics
  championsRedirectRoute,
  championsRoute,

  // Single champion
  championRedirectRoute,
  championRoute,

  // Tier list
  tierlistRoute,

  // Pro builds
  probuildsRoute,
  probuildsRedirectRoute,

  // Arena queue
  {
    path: "/lol/arena",
    component: "game-lol/ArenaAugmentList.jsx",
  },
  {
    path: "/lol/arena/augments",
    component: "game-lol/ArenaAugments.jsx",
    fetchData: lolFetchArenaAugments,
  },
  {
    path: new RegExp("/lol/.*?(arena-mode)"),
    redirect: (route) => route.currentPath.replace(/arena-mode/, "arena"),
  },

  // Patch Notes
  {
    path: "/lol/patchnotes",
    redirect: "/lol/patchnotes/latest",
  },
  {
    path: pathRegExp("/lol/patchnotes/:patch(\\d+.\\d+|latest)"),
    component: "game-lol/PatchNotes.jsx",
  },

  // Season Recap
  {
    path: pathRegExp("/lol/season-review/:season"),
    component: "game-lol/SeasonReviewSearch.tsx",
  },
  {
    path: pathRegExp("/lol/season-review/:region/:name/:season"),
    component: "game-lol/SeasonReview.tsx",
    // fetchData: lolFetchSeasonReview,
  },
  {
    path: pathRegExp("/lol/profile/:region/:name/season-review/:season"),
    redirect: (route) => {
      const [region, name, season] = route.parameters;
      return `/lol/season-review/${region}/${name}/${season}`;
    },
  },

  {
    path: pathRegExp("/lol/article/:id"),
    component: "game-lol/Article.tsx",
    fetchData: fetchArticle,
  },
  {
    path: pathRegExp("/lol/guides"),
    component: "game-lol/GuidesList.tsx",
    fetchData: fetchGuidesList,
  },
  {
    path: pathRegExp("/lol/news"),
    component: "game-lol/News.tsx",
    fetchData: fetchNewsList,
  },
  {
    path: pathRegExp("/lol/doombots-leaderboard"),
    component: "game-lol/DoombotsLeaderboard.tsx",
    fetchData: lolFetchDoombotsLeaderboard,
  },

  {
    path: pathRegExp("/lol/aram-mayhem-augments"),
    component: "game-lol/AramMayhemAugments.tsx",
    fetchData: lolFetchAramMayhemAugmentsStats,
  },
];

routes.push({
  [catchAllRoutes]: routes,
  fetchData: lolFetchStaticData,
});

export default routes;
